import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Award, Brain, HeartHandshake, ShieldCheck, Sparkles, Star, Users } from "lucide-react";
import { http } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import LeadMagnetForm from "@/components/LeadMagnetForm";
import CourseCard from "@/components/CourseCard";
import { Seo } from "@/components/Seo";

const HERO_IMG = "https://images.unsplash.com/photo-1626187429639-7a77bfad0523?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1574175679797-9fc9eade1450?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const METHOD_IMG = "https://images.unsplash.com/photo-1596464716127-f2a82984de30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const STATS = [
  { value: "15 ans", label: "d'expérience terrain" },
  { value: "1 200+", label: "professionnels formés" },
  { value: "4.9/5", label: "de satisfaction" },
  { value: "8", label: "formations expertes" },
];

export default function HomePage() {
  const [formations, setFormations] = useState([]);
  const { t } = useLang();
  useEffect(() => { http.get("/formations").then((r) => setFormations(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <Seo description="Formations en neurosciences appliquées à la petite enfance pour les professionnels et structures d'accueil. Guides Harvard gratuits, formations sur devis." />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-50 via-[#FDF9EC] to-white" data-testid="hero-section">
        <div className="absolute inset-0 noise-overlay opacity-40 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="container-x pt-14 pb-24 lg:pt-20 lg:pb-32 grid lg:grid-cols-12 gap-14 items-center relative">
          <div className="lg:col-span-7 animate-in fade-in slide-in-from-bottom-8 duration-700 fill-mode-both">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold text-brand-800 mb-7 shadow-sm">
              <Sparkles size={12} strokeWidth={2} /> {t("hero_badge")}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.12] tracking-tight max-w-2xl">
              {t("hero_title_1")} <span className="text-brand-600">{t("hero_title_accent")}</span> {t("hero_title_2")}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              {t("hero_sub")}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/formations" data-testid="hero-cta-b2c" className="btn-primary shadow-lg shadow-brand-500/25">
                {t("hero_cta_b2c")} <ArrowRight size={18} strokeWidth={2} />
              </Link>
              <Link to="/offre" data-testid="hero-cta-b2b" className="btn-outline bg-white/60 backdrop-blur">
                {t("hero_cta_b2b")}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
              <span className="flex items-center gap-2"><Award size={16} strokeWidth={1.5} className="text-brand-600" /> {t("hero_badge_cpf")}</span>
              <span className="flex items-center gap-2"><Users size={16} strokeWidth={1.5} className="text-brand-600" /> {t("hero_badge_pros")}</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.5} className="text-brand-600" /> Contenus fondés sur la science</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-in fade-in slide-in-from-bottom-8 duration-700 fill-mode-both [animation-delay:200ms]">
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgb(0_0_0_/_0.12)] border border-white/60">
              <img src={HERO_IMG} alt="Jeune enfant concentré sur un matériel pédagogique en bois" className="w-full h-[460px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-8 -left-10 hidden md:block w-48 rounded-3xl overflow-hidden border-4 border-white shadow-xl rotate-[-3deg]">
              <img src={HERO_IMG_2} alt="Détail d'un matériel pédagogique en bois" className="h-32 w-full object-cover" />
            </div>
            <div className="absolute -top-5 -right-4 hidden md:block card-soft p-4 w-52 backdrop-blur bg-white/95">
              <div className="flex items-center gap-1 mb-1 text-amber-500">
                {[...Array(5)].map((_, i) => <Star key={`star-${i}`} size={13} fill="currentColor" strokeWidth={0} />)}
              </div>
              <div className="text-sm font-semibold text-slate-900">4.9 / 5 en moyenne</div>
              <div className="text-xs text-slate-500">sur 380 professionnels formés en 2025</div>
            </div>
            <div className="absolute top-1/2 -left-8 hidden lg:flex card-soft p-3.5 items-center gap-3 backdrop-blur bg-white/95">
              <div className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600">
                <Brain strokeWidth={1.5} size={20} />
              </div>
              <div>
                <div className="text-xs text-slate-500">Neurosciences validées</div>
                <div className="text-sm font-semibold text-slate-900">Contenus scientifiques</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-slate-100 bg-white" data-testid="stats-strip">
        <div className="container-x py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={s.label} className="text-center md:text-left animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both" style={{ animationDelay: `${i * 120}ms` }}>
              <div className="font-display text-3xl lg:text-4xl font-bold text-brand-600">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* POURQUOI BEEHIVE ? */}
      <section className="container-x py-24" data-testid="benefits-section">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Pourquoi Beehive Academy ?</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Parce que mieux comprendre l'enfant, c'est mieux l'accompagner.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Au fil de nos observations dans les structures d'accueil, une conviction s'est imposée : la compréhension de l'enfant par l'adulte est au cœur d'un accompagnement de qualité.</p>
            <p className="mt-3 text-slate-600 leading-relaxed">Comprendre ce que l'enfant vit, ce dont il a besoin, ce qu'il cherche à exprimer et ce qui se joue derrière ses comportements permet à l'adulte d'ajuster sa posture et ses pratiques.</p>
            <p className="mt-3 text-slate-600 leading-relaxed">C'est de cette réalité du terrain qu'est née Beehive Academy : un espace de formation qui donne aux professionnels de la petite enfance les connaissances et les outils nécessaires pour mieux comprendre les enfants qu'ils accompagnent et adapter leurs pratiques à leurs besoins.</p>
            <p className="mt-3 text-slate-700 leading-relaxed font-medium">Notre ambition est de rendre les connaissances issues des neurosciences et du développement de l'enfant accessibles, concrètes et directement utiles au quotidien.</p>
            <div className="mt-8 rounded-[2rem] overflow-hidden border border-slate-200/60 group">
              <img src={METHOD_IMG} alt="Mains d'enfant en pleine activité créative" className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </div>
          <div className="lg:col-span-7 grid gap-6 sm:grid-cols-2">
            <div className="card-soft p-8 sm:col-span-2 bg-brand-500 border-brand-500 text-white transition-transform hover:-translate-y-1" style={{ transitionProperty: "transform, box-shadow" }}>
              <div className="grid place-items-center h-12 w-12 rounded-2xl bg-white/15 text-white mb-5"><Brain strokeWidth={1.5} /></div>
              <h3 className="font-display font-bold text-xl">Comprendre le cerveau</h3>
              <p className="mt-2 text-white/85 text-sm leading-relaxed max-w-md">Les mécanismes clés du développement de 0 à 5 ans, expliqués simplement, avec des repères visuels clairs.</p>
            </div>
            <div className="card-soft p-8 transition-transform hover:-translate-y-1 hover:shadow-lg" style={{ transitionProperty: "transform, box-shadow" }}>
              <div className="grid place-items-center h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-700 mb-5"><HeartHandshake strokeWidth={1.5} /></div>
              <h3 className="font-display font-bold text-xl text-slate-900">Appliquer au quotidien</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">Des gestes concrets à mettre en pratique au quotidien.</p>
            </div>
            <div className="card-soft p-8 transition-transform hover:-translate-y-1 hover:shadow-lg" style={{ transitionProperty: "transform, box-shadow" }}>
              <div className="grid place-items-center h-12 w-12 rounded-2xl bg-slate-900 text-white mb-5"><Award strokeWidth={1.5} /></div>
              <h3 className="font-display font-bold text-xl text-slate-900">Certifier vos acquis</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">Une attestation de participation est délivrée à l'issue de chaque formation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FORMATIONS */}
      <section className="bg-[#FDFBF7] border-y border-slate-100" data-testid="formations-section">
        <div className="container-x py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow mb-3">{t("catalog_eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight max-w-xl">{t("catalog_title")}</h2>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">Consultez notre catalogue : choisissez les solutions qui correspondent à votre profil et à votre rythme.</p>
            </div>
            <Link to="/formations" data-testid="home-catalog-link" className="text-sm font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 shrink-0">
              {t("catalog_see_all")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {formations.slice(0, 3).map((f) => <CourseCard key={f.slug} f={f} />)}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/formations" data-testid="home-catalog-cta" className="btn-outline">
              Explorer les 8 formations <ArrowUpRight size={16} strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section className="container-x py-24 grid lg:grid-cols-2 gap-14 items-center" data-testid="lead-magnet-section">
        <div>
          <div className="eyebrow mb-3">Ressources offertes</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Des guides de référence.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Des guides sur le développement cérébral du jeune enfant, issus du Center on the Developing Child de l'Université Harvard — en accès libre.</p>
          <ul className="mt-6 space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><Sparkles size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />7 guides PDF de référence</li>
            <li className="flex gap-3"><Sparkles size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />Fonctions exécutives, science du développement</li>
            <li className="flex gap-3"><Sparkles size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />Activités concrètes par tranche d'âge, de 6 mois à 5 ans</li>
          </ul>
          <Link to="/guides" data-testid="home-guides-link" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-800">
            Voir tous les guides <ArrowRight size={14} />
          </Link>
        </div>
        <div><LeadMagnetForm /></div>
      </section>

      {/* CTA FINALE */}
      <section className="container-x pb-24" data-testid="final-cta">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-900 text-white p-12 lg:p-16">
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-500/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-400/15 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Prêt à renforcer vos pratiques professionnelles ?</h2>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">Rejoignez les professionnels qui donnent du sens à chaque geste, chaque jour, auprès des enfants.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/inscription" data-testid="final-cta-register" className="btn-primary shadow-lg shadow-brand-500/30">Créer mon compte gratuit</Link>
              <Link to="/formations" data-testid="final-cta-catalog" className="btn-outline border-slate-700 text-white hover:bg-slate-800">Explorer les formations</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ILS NOUS FONT CONFIANCE — bas de page, format discret */}
      <section className="border-t border-slate-100" data-testid="trust-banner">
        <div className="container-x py-10">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Ils nous font confiance</div>
            <div className="flex items-center justify-center gap-2.5 mb-2">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#A51C30]/10 text-[#A51C30]">
                <ShieldCheck size={16} strokeWidth={1.5} />
              </span>
              <span className="font-display font-bold text-sm text-slate-800">Center on the Developing Child — Université Harvard</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Des contenus fondés sur des références scientifiques reconnues. Nos formations et nos guides s'appuient sur les travaux de référence du Center on the Developing Child de l'Université Harvard, autorité mondiale en science du développement de l'enfant.
            </p>
            <a
              href="https://developingchild.harvard.edu/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trust-harvard-link"
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800"
            >
              developingchild.harvard.edu <ArrowUpRight size={12} strokeWidth={2} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
